import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { or } from 'ember-truth-helpers'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'

interface TableContainerSignature {
  Element: HTMLDivElement
  Blocks: {
    default: []
  }
}

/**
 * Provides a scrollable container for a table
 */
export const TableContainer: TOC<TableContainerSignature> = <template>
  <div class="overflow-x-auto overflow-y-hidden print:overflow-visible" ...attributes>{{yield}}</div>
</template>

interface TableSignature {
  Element: HTMLTableElement
  Args: {
    /* can be helpful to inform container when to begin scroll */
    minWidth?: string
  }
  Blocks: {
    default: []
  }
}

/**
 * Provides a fixed-layout table
 *
 * You may want to provide some width classes on your TH cells (though not required)
 */
export const Table: TOC<TableSignature> = <template>
  <table class="w-full table-fixed {{@minWidth}}" ...attributes>{{yield}}</table>
</template>

interface TrHeadSignature {
  Element: HTMLTableRowElement
  Blocks: {
    default: []
  }
}

export class TRHead extends Component<TrHeadSignature> {
  @service subscriptionType!: SubscriptionTypeService

  get themeClass() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return 'bg-oceany-blue-100'
      case SubscriptionType.Maths:
        return 'bg-ms-green-100'
      default:
        return 'bg-oceany-blue-100'
    }
  }

  <template>
    <tr class="{{this.themeClass}}" ...attributes>{{yield}}</tr>
  </template>
}

interface ThSignature {
  Element: HTMLTableCellElement
  Args: {
    /** If provided, will get/set sort query param & render a sort icon, and make the cell clickable */
    sortKey?: string
    /* Otherwise, use this to indicate the header will be clickable for some purpose - will apply pointer etc. */
    clickable?: boolean
  }
  Blocks: { default: [] }
}

/**
 * Provides a standard `<th>` to use, that applies the standard styles we expect from header cells. It can accept a
 * sortKey (which will display an icon), and other items (like tooltip icons) are also permitted.
 *
 * @example
 * <TH @sortKey="optional_key_to_sort_by">
 *   My text
 *   <OptionalTooltipIcon />
 * </TH>
 * </code>
 */
export const TH: TOC<ThSignature> = <template>
  {{#if @sortKey}}
    <ThSorter class="p-3 font-bold text-neutral-300" @sortKey={{@sortKey}} ...attributes>{{yield}}</ThSorter>
  {{else}}
    <ThIcons class="p-3 font-bold text-neutral-300" @clickable={{@clickable}} ...attributes>{{yield}}</ThIcons>
  {{/if}}
</template>

interface TdSignature {
  Element: HTMLTableCellElement
  Args: { textColor?: string }
  Blocks: { default: [] }
}

/**
 * Provides a standard `<td>` to use, that applies the standard styles we expect from body cells.
 * wrap-break-word will wrap nicely at first (breaking on spaces), when even less room will begin to break on words.
 */
export const TD: TOC<TdSignature> = <template>
  <td class="p-3 wrap-break-word {{or @textColor 'text-neutral-300'}}" ...attributes>{{yield}}</td>
</template>

interface ThIconsSignature {
  Element: HTMLTableCellElement
  Args: {
    clickable?: boolean
  }
  Blocks: {
    default: []
  }
}

/**
 * Use this to add zero or more icons to a th cell in a standard way
 *
 * @example
 * <ThIcons>
 *   My Text
 *   <TooltipIcon />
 *   <SortIcon />
 * </ThIcons>
 */
export const ThIcons: TOC<ThIconsSignature> = <template>
  <th class={{if @clickable "cursor-pointer select-none"}} ...attributes>
    <div class="inline-flex items-center justify-start gap-2 whitespace-nowrap">
      {{yield}}
    </div>
  </th>
</template>

interface ThSorterSignature {
  Element: HTMLTableCellElement
  Args: {
    sortKey: string
  }
  Blocks: {
    default: []
  }
}

/**
 * Get & sets sort query param to display sort icon & handle click, makes use of <ThIcons /> component to display icon.
 *
 * @example
 * <ThSorter @sortKey="key_to_sort">
 *   My Text
 *   <OptionalTooltipIcon />
 * </ThSorter>
 */
export class ThSorter extends Component<ThSorterSignature> {
  @service router!: RouterService

  get sort(): string | undefined {
    /**
     * When transitioning to a page with sorting, often the query param will not be set in the URL. The default value
     * to use when that happens is defined in the controller.
     *
     * When obtaining the sort via the router service, we do not have access to that value - the router service will
     * provide the sort query param as "undefined". In practice, for this component it just means that the sort icon
     * will not be we shown on initial load if there is no sort QP in the URL. It will just be the generic up+down icon.
     *
     * The alternative is to locate the correct controller that should have the sort, and get/set that tracked property,
     * but as of writing this cannot be done in a generic page-agnostic way.
     */
    const sortParam = this.router.currentRoute?.find((rInfo) => Boolean(rInfo.queryParams.sort))?.queryParams.sort
    return typeof sortParam === 'string' ? sortParam : undefined
  }

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  updateSort = () => {
    const newSort = serializeSort(updateSortColumns(this.sortingConfig, this.args.sortKey))

    void this.router.replaceWith({ queryParams: { sort: newSort } })
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <ThIcons
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      @clickable={{true}}
      {{! template-lint-disable no-invalid-interactive }}
      {{on "click" this.updateSort}}
      ...attributes
    >
      {{yield}}
      <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-neutral-300 print:hidden" />
    </ThIcons>
  </template>
}
